/**
 * SHELL — claim/upgrade screen. Converts the anonymous-first player into a real
 * account WITHOUT losing their streak (Supabase links the identity to the same
 * user id). Offered only when there's something to save.
 */

import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el, button } from '../dom';
import { brand, banner, iconButton, streakBadge } from '../components';
import { toast } from '../toast';

export function claimScreen(app: App): Screen {
  const snap = app.store.get();
  const screen = div('dr-screen');
  const card = div('dr-card');

  card.append(brand());
  card.append(el('h2', { class: 'dr-h', text: 'Claim your streak' }));
  if (snap.streak.current > 0) card.append(streakBadge(snap.streak.current, { best: snap.streak.best }));
  card.append(el('p', { class: 'dr-muted', text: 'Save your progress across devices and unlock friend leaderboards. No password — we email you a magic link.' }));

  const status = div('');

  // Email magic-link form.
  const emailInput = el('input', {
    class: 'dr-btn dr-btn--block',
    type: 'email',
    attrs: { placeholder: 'you@example.com', autocomplete: 'email', inputmode: 'email' },
    style: { background: 'var(--bg-elev-2)', textAlign: 'left', justifyContent: 'flex-start' },
  });
  emailInput.style.padding = '0 18px';

  const submit = button('dr-btn dr-btn--primary dr-btn--block', 'Email me a link', () => void doEmail());
  card.append(emailInput, submit, status);

  // OAuth options (available when configured server-side).
  card.append(el('hr', { class: 'dr-divider' }));
  card.append(
    div('dr-btn-row',
      button('dr-btn dr-btn--ghost', 'Continue with Google', () => void doOAuth('google')),
      button('dr-btn dr-btn--ghost', 'Continue with Apple', () => void doOAuth('apple')),
    ),
  );

  screen.append(card);
  screen.append(iconButton('dr-btn dr-btn--ghost dr-btn--block', 'back', 'Maybe later', () => app.go(snap.dailyStatus === 'completed' ? 'results' : 'landing')));

  async function doEmail(): Promise<void> {
    const email = (emailInput as HTMLInputElement).value;
    submit.disabled = true;
    status.replaceChildren(banner('info', 'Sending…'));
    const res = await app.auth.claimWithEmail(email);
    submit.disabled = false;
    if (res.ok) {
      status.replaceChildren(banner('info', `Check ${email} for your magic link. Open it on this device to finish.`));
    } else {
      status.replaceChildren(banner('err', res.error ?? 'Something went wrong.'));
    }
  }

  async function doOAuth(provider: 'google' | 'apple'): Promise<void> {
    const res = await app.auth.claimWithOAuth(provider);
    if (!res.ok) toast(res.error ?? 'Sign-in unavailable.');
    // On success the provider redirect takes over; nothing more to do here.
  }

  return { node: screen };
}
